// Swiper mode — clock-swipe card encoding + localized card formatting.
// The magician transmits a card with three directional flicks performed
// silently on the decoy Notes screen. The captured card is rendered into
// the prediction note body using the magician's chosen format/template.

import { STORAGE } from "./storage";

const SUIT_SYMBOLS = { S: "♠", H: "♥", C: "♣", D: "♦" };

/**
 * Localized playing-card vocabulary.
 *
 * Each entry defines how a rank+suit pair is spelled out in that language,
 * the word order ("value of suit" vs "suit value"), and the language's
 * word for the Joker. Keys are the short language codes used elsewhere in
 * this file (the prefix of BCP-47 codes — "nl" for "nl-NL"/"nl-BE", etc.).
 * Languages not listed fall back to English.
 *
 * `long(v, s)` always returns Title Case suitable for a standalone
 * prediction (e.g. "Queen of Hearts" / "Harten Vrouw"). When the magician
 * uses a custom template like "Mijn voorspelling is (xxx)", the result is
 * lowercased before substitution so it reads naturally mid-sentence.
 */
const LOCAL_CARDS = {
  en: {
    values: { A:"Ace",  "2":"Two",  "3":"Three",  "4":"Four",  "5":"Five",  "6":"Six",  "7":"Seven", "8":"Eight", "9":"Nine", "10":"Ten",   J:"Jack",  Q:"Queen", K:"King" },
    suits:  { S:"Spades",   H:"Hearts",   C:"Clubs",     D:"Diamonds" },
    joker:  "Joker",
    long:   (v, s) => `${v} of ${s}`,
  },
  nl: {
    values: { A:"Aas",  "2":"Twee", "3":"Drie",   "4":"Vier",  "5":"Vijf",  "6":"Zes",  "7":"Zeven", "8":"Acht",  "9":"Negen","10":"Tien",  J:"Boer",  Q:"Vrouw", K:"Heer" },
    suits:  { S:"Schoppen", H:"Harten",   C:"Klaveren",  D:"Ruiten"  },
    joker:  "Joker",
    long:   (v, s) => `${s} ${v}`,
  },
  de: {
    values: { A:"Ass",  "2":"Zwei", "3":"Drei",   "4":"Vier",  "5":"Fünf",  "6":"Sechs","7":"Sieben","8":"Acht",  "9":"Neun", "10":"Zehn",  J:"Bube",  Q:"Dame",  K:"König" },
    suits:  { S:"Pik",      H:"Herz",     C:"Kreuz",     D:"Karo"    },
    joker:  "Joker",
    long:   (v, s) => `${s} ${v}`,
  },
  fr: {
    values: { A:"As",   "2":"Deux", "3":"Trois",  "4":"Quatre","5":"Cinq",  "6":"Six",  "7":"Sept",  "8":"Huit",  "9":"Neuf", "10":"Dix",   J:"Valet", Q:"Dame",  K:"Roi" },
    suits:  { S:"Pique",    H:"Cœur",     C:"Trèfle",    D:"Carreau" },
    joker:  "Joker",
    long:   (v, s) => `${v} de ${s}`,
  },
  es: {
    values: { A:"As",   "2":"Dos",  "3":"Tres",   "4":"Cuatro","5":"Cinco", "6":"Seis", "7":"Siete", "8":"Ocho",  "9":"Nueve","10":"Diez",  J:"Jota",  Q:"Reina", K:"Rey" },
    suits:  { S:"Picas",    H:"Corazones",C:"Tréboles",  D:"Diamantes" },
    joker:  "Comodín",
    long:   (v, s) => `${v} de ${s}`,
  },
  it: {
    values: { A:"Asso", "2":"Due",  "3":"Tre",    "4":"Quattro","5":"Cinque","6":"Sei",  "7":"Sette", "8":"Otto",  "9":"Nove", "10":"Dieci", J:"Fante", Q:"Regina",K:"Re" },
    suits:  { S:"Picche",   H:"Cuori",    C:"Fiori",     D:"Quadri"  },
    joker:  "Jolly",
    long:   (v, s) => `${v} di ${s}`,
  },
  pt: {
    values: { A:"Ás",   "2":"Dois", "3":"Três",   "4":"Quatro","5":"Cinco", "6":"Seis", "7":"Sete",  "8":"Oito",  "9":"Nove", "10":"Dez",   J:"Valete",Q:"Rainha",K:"Rei" },
    suits:  { S:"Espadas",  H:"Copas",    C:"Paus",      D:"Ouros"   },
    joker:  "Curinga",
    long:   (v, s) => `${v} de ${s}`,
  },
};

/**
 * Pick the localized card vocabulary for a given language code. Accepts
 * either short ("nl") or BCP-47 ("nl-NL") codes; falls back to English
 * when the language isn't fully translated.
 */
function pickCardLocale(langCode) {
  if (!langCode || langCode === "auto") {
    // Honour the browser's preferred UI language when the magician left
    // Settings on "Auto-detect" — keeps the prediction in the same
    // language the disguise UI is rendering.
    const browser = typeof navigator !== "undefined" && navigator.language
      ? navigator.language
      : "en";
    const short = String(browser).toLowerCase().split("-")[0];
    return LOCAL_CARDS[short] || LOCAL_CARDS.en;
  }
  const short = String(langCode).toLowerCase().split("-")[0];
  return LOCAL_CARDS[short] || LOCAL_CARDS.en;
}

/**
 * Clock-swipe encoding. The magician transmits a card with up to three
 * directional flicks performed silently on the decoy Notes screen.
 *
 * First two swipes — the VALUE (matches the magician's "clock face" diagram):
 *   U+U  → Q     R+U → 2     D+R → 5     L+D → 8
 *   U+R  → A     R+R → 3     D+D → 6     L+L → 9
 *   U+D  → K     R+D → 4     D+L → 7     L+U → 10
 *   U+L  → J     R+L → JOKER (suit then skipped)
 *
 * Third swipe — the SUIT:
 *   U → ♠ Spades · R → ♥ Hearts · D → ♣ Clubs · L → ♦ Diamonds
 *
 * Joker has no suit and locks in after the second swipe.
 */
const CLOCK_VALUE_TABLE = {
  UU: "Q",  UD: "K",  UR: "A", UL: "J",
  RU: "2",  RR: "3",  RD: "4", RL: "JOKER",
  DR: "5",  DD: "6",  DL: "7",
  LD: "8",  LL: "9",  LU: "10",
  // DU and LR are intentionally unmapped — they reset the buffer.
};
const CLOCK_SUIT_TABLE = { U: "S", R: "H", D: "C", L: "D" };

/**
 * Read the magician-configured Swiper config from localStorage.
 *
 * `enabled` is the master switch. `mode` toggles between the canonical
 * 3-swipe playing-card encoding ("cards") and the magician's own 4-word
 * single-swipe encoding ("custom"). `format`/`template` are the
 * cards-mode prediction-text settings; `words` is the {U,R,D,L} word
 * vocabulary used in custom mode.
 */
export function getSwiperConfig() {
  const mode = localStorage.getItem(STORAGE.SWIPER_MODE);
  return {
    enabled: localStorage.getItem(STORAGE.SWIPER_ENABLED) === "1",
    mode: mode === "custom" ? "custom" : "cards",
    format: localStorage.getItem(STORAGE.SWIPER_FORMAT) || "custom",
    template: localStorage.getItem(STORAGE.SWIPER_TEMPLATE) || "",
    combos: loadSwiperCombos(),
  };
}

/**
 * Persist the magician's custom combo list. Each entry is
 * `{id, dirs, word}` where `dirs` is 1–3 chars from "URDL" describing
 * the swipe sequence ("URD" = ↑→↓), and `word` is the substitution.
 *
 * Bypasses the legacy single-direction WORD_U/R/D/L keys entirely —
 * once a list is saved here, those keys are ignored even if they hold
 * stale values from a previous version.
 */
export function saveSwiperCombos(list) {
  try {
    const cleaned = (Array.isArray(list) ? list : [])
      .map((c) => ({
        id: c?.id || makeComboId(),
        dirs: typeof c?.dirs === "string" ? c.dirs.toUpperCase().replace(/[^URDL]/g, "").slice(0, 3) : "",
        word: typeof c?.word === "string" ? c.word : "",
      }))
      .filter((c) => c.dirs.length >= 1);
    localStorage.setItem(STORAGE.SWIPER_CUSTOM_COMBOS, JSON.stringify(cleaned));
  } catch {
    /* ignore quota/serialise errors — settings layer handles UX */
  }
}

/**
 * Load magician-defined combos. Falls back to seeding from the legacy
 * single-direction word keys (Feb 2026 v1) when no combos have been
 * saved yet, so users who upgrade keep their setup.
 */
function loadSwiperCombos() {
  try {
    const raw = localStorage.getItem(STORAGE.SWIPER_CUSTOM_COMBOS);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .filter((c) => c && typeof c.dirs === "string" && c.dirs.length >= 1 && typeof c.word === "string")
          .map((c) => ({ id: c.id || makeComboId(), dirs: c.dirs.toUpperCase(), word: c.word }));
      }
    }
  } catch { /* ignore */ }
  // Legacy migration — turn the four single-direction words into
  // single-flick combos so the magician's existing setup keeps working.
  const legacy = [
    ["U", STORAGE.SWIPER_WORD_U],
    ["R", STORAGE.SWIPER_WORD_R],
    ["D", STORAGE.SWIPER_WORD_D],
    ["L", STORAGE.SWIPER_WORD_L],
  ]
    .map(([dirs, key], i) => ({ id: `legacy-${i}`, dirs, word: localStorage.getItem(key) || "" }))
    .filter((c) => c.word);
  return legacy;
}

/** Short, collision-safe id for combo rows. */
function makeComboId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `combo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const makeSwiperComboId = makeComboId;

/**
 * Capture a custom-mode word triggered by a single direction flick.
 * Stored under the same SWIPER_CARD key the cards-mode pipeline uses,
 * but marked `custom: true` so formatSwiperCard knows to treat
 * `value` as the literal substitution word.
 */
export function setSwiperCustomWord(word) {
  try {
    localStorage.setItem(
      STORAGE.SWIPER_CARD,
      JSON.stringify({ value: String(word || ""), suit: null, custom: true })
    );
    _scheduleSwiperSnapshotSync();
  } catch {
    /* localStorage may be full or disabled — fine to drop silently. */
  }
}

/**
 * Look up just the value (rank or "JOKER") from the first two swipes.
 * Returns null when the combination isn't in the table — caller can use
 * that as a signal to drop the buffered swipes and start fresh.
 */
export function decodeClockValue(twoSwipes) {
  if (!twoSwipes || twoSwipes.length < 2) return null;
  return CLOCK_VALUE_TABLE[twoSwipes[0] + twoSwipes[1]] || null;
}

/**
 * Decode a complete three-swipe sequence (or two, for Joker) into a
 * card object `{ value, suit }`. Returns null when the sequence is
 * malformed (unknown value combo, or suit swipe missing/invalid for
 * non-Joker values).
 */
export function decodeClockCard(swipes) {
  const value = decodeClockValue(swipes);
  if (!value) return null;
  if (value === "JOKER") return { value: "JOKER", suit: null };
  if (!swipes || swipes.length < 3) return null;
  const suit = CLOCK_SUIT_TABLE[swipes[2]];
  if (!suit) return null;
  return { value, suit };
}

/**
 * Persist the freshly encoded card so the trick screen can pick it up
 * when the magician opens the prediction note.
 */
export function setSwiperCard({ value, suit }) {
  try {
    localStorage.setItem(STORAGE.SWIPER_CARD, JSON.stringify({ value, suit }));
    _scheduleSwiperSnapshotSync();
  } catch {
    /* localStorage may be full or disabled — fine to drop silently. */
  }
}

export function readSwiperCard() {
  try {
    const raw = localStorage.getItem(STORAGE.SWIPER_CARD);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.value) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearSwiperCard() {
  localStorage.removeItem(STORAGE.SWIPER_CARD);
  _scheduleSwiperSnapshotSync();
}

/**
 * Build the display string for the captured card.
 *
 * Format:
 *   long    -> Title Case in the current language (e.g. "Queen of Hearts",
 *              "Harten Vrouw", "Dame de Cœur") — suitable as a standalone
 *              prediction line.
 *   symbol  -> compact international notation: "Q♥" (Joker → "🃏").
 *   custom  -> magician-supplied sentence template. The placeholder
 *              `(xxx)` is replaced by the card name in the current
 *              language (lowercased so it reads naturally mid-sentence).
 *              Example (Dutch):
 *                "Mijn voorspelling is dat jij de (xxx) kiest!"
 *                → "Mijn voorspelling is dat jij de harten vrouw kiest!"
 *              If the template is empty OR contains no `(xxx)` placeholder,
 *              we fall back to the bare long-form card name.
 *              (Legacy curly placeholders `{xxx}` / `{card}` / `{symbol}`
 *               still work silently for previously-saved templates.)
 *
 * `langCode` is the user's full language code from Settings (e.g. "nl-NL");
 * the short prefix selects the right localization.
 */
export function formatSwiperCard(card, format, template, langCode) {
  if (!card || !card.value) return "";

  // --- Custom-word mode ---
  // The magician's single-direction flick captured a literal word.
  // Drop it into the template's (xxx) placeholder; if there's no
  // template (or no placeholder), fall back to just the bare word.
  if (card.custom) {
    const word = String(card.value);
    if (template && /\(xxx\)/.test(template)) {
      return template.replace(/\(xxx\)/g, word);
    }
    return word;
  }

  const L = pickCardLocale(langCode);

  // Joker is suit-less in every language.
  if (card.value === "JOKER") {
    if (format === "symbol") return "🃏";
    if (format === "custom" && template && /\(xxx\)|\{xxx\}|\{card\}|\{symbol\}/.test(template)) {
      return template
        .replace(/\(xxx\)/g, L.joker.toLowerCase())
        .replace(/\{xxx\}/g, L.joker.toLowerCase())
        .replace(/\{card\}/g, L.joker.toLowerCase())
        .replace(/\{symbol\}/g, "🃏");
    }
    return L.joker;
  }

  if (!card.suit) return "";
  const vWord = L.values[card.value] || card.value;
  const sWord = L.suits[card.suit] || card.suit;
  const long  = L.long(vWord, sWord);
  const symbol = `${card.value}${SUIT_SYMBOLS[card.suit] || ""}`;

  if (format === "symbol") return symbol;
  if (format === "custom" && template && /\(xxx\)|\{xxx\}|\{card\}|\{symbol\}/.test(template)) {
    return template
      .replace(/\(xxx\)/g, long.toLowerCase())
      .replace(/\{xxx\}/g, long.toLowerCase())
      .replace(/\{card\}/g, long.toLowerCase())
      .replace(/\{symbol\}/g, symbol);
  }
  return long;
}

// ------------------------------------------------------------------
// Shared-note snapshot auto-sync. Every set/clear above notifies the
// active /n/{code} snapshot so a card swiped on /notes is immediately
// reflected on the spectator's genie/note page. Uses a dynamic
// import to avoid the swiper.js ↔ predictionLink.js cycle. Debounces
// so a burst of localStorage writes coalesces into a single PATCH.
// ------------------------------------------------------------------
let _syncTimer = null;
function _scheduleSwiperSnapshotSync() {
  if (typeof window === "undefined") return;
  if (_syncTimer) clearTimeout(_syncTimer);
  _syncTimer = setTimeout(async () => {
    _syncTimer = null;
    try {
      const mod = await import("./predictionLink");
      if (mod && typeof mod.syncActiveSnapshot === "function") {
        mod.syncActiveSnapshot();
      }
    } catch { /* silent — offline / bundler issue */ }
  }, 400);
}

